import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithReset } from 'jotai/utils';

import { batchIdAtom, materialsAtom } from '@/_atoms/batch';
import { isTimerPausedAtom, contextNavAtom } from '@/_atoms/navigation';
import { surveyIdAtom, surveyViewAtom } from '@/_atoms/survey';
import { answeredElementsAtom } from '@/_atoms/answer';
import { getNumberOfAnsweredElements } from '@/_helpers/progressBarContainerHelpers';

import type { Material } from '@/types/batch';
import type { Alert } from '@/types/component';
import type { Progression } from '@/types/respondent';
import type { StepInfo } from '@/types/survey';

/** An Alert box to display in celebration of completing a step. */
export const celebrationAtom = atomWithReset<Alert | null>(null);

/** Set of ids for completed steps. */
export const completedStepIdsAtom = atom<Set<string>>((get) => {
  const progressions: Progression[] = get(progressionsAtom);
  const completedStepIds = new Set<string>();
  for (const p of progressions) {
    if (p.type === 'C' && !p.deletedAt && !p.ignoredAt && typeof p.step === 'string') completedStepIds.add(p.step);
  }
  return completedStepIds;
});

/** Set of ids for completed surveys. */
export const completedSurveyIdsAtom = atom<Set<string>>((get) => {
  const progressions: Progression[] = get(progressionsAtom);
  const completedSurveyIds = new Set<string>();
  for (const p of progressions) {
    if (p.type === 'C' && !p.deletedAt && !p.ignoredAt && typeof p.survey === 'string') completedSurveyIds.add(p.survey);
  }
  return completedSurveyIds;
});

/**
 * Determine if the respondent has completed all required materials. Boolean.
 */
export const hasCompletedAllRequiredMaterialsAtom = atom((get) => {
  const materials: Material[] = get(materialsAtom);
  const progressions: Progression[] = get(progressionsAtom);

  if (materials.length === 0 || progressions.length === 0) return false;

  return materials
    .filter((m: Material) => m.isRequired)
    .every((m: Material) =>
      progressions.some((p: Progression) => p.survey === m.survey && p.type === 'C' && !p.deletedAt)
    );
});

/**
 * Determine if the respondent has a progression marking the batch as completed.
 */
export const hasFinishedBatchAtom = atom<boolean>((get) => {
  const batchId: string | null = get(batchIdAtom);
  const progressions: Progression[] = get(progressionsAtom);

  if (!batchId || !progressions?.length) return false;

  return progressions.some((p: Progression) =>
    p.batch === batchId &&
    p.type === 'C' &&
    !p.deletedAt
  );
});

/**
 * Determine if the respondent has a progression marking the batch as started.
 */
export const hasStartedBatchAtom = atom<boolean>((get) => {
  const batchId: string | null = get(batchIdAtom);
  const progressions: Progression[] = get(progressionsAtom);

  if (!batchId || !progressions?.length) return false;

  return progressions.some((p: Progression) =>
    p.batch === batchId &&
    p.type === 'S' &&
    !p.deletedAt
  );
});

/** Is there any completed material after the given one? */
export const hasSubsequentCompletedMaterialAtomFamily = atomFamily((position: number) =>
  atom((get) => {
    const materials: Material[] = get(materialsAtom);
    const completedSurveyIds: Set<string> = get(completedSurveyIdsAtom);

    for (const m of materials) {
      if (m.position > position && completedSurveyIds.has(m.survey)) {
        return true;
      }
    }
    return false;
  })
);

/** Has the current survey been completed? */
export const isCurrentSurveyCompletedAtom = atom((get) => {
  const surveyId: string | null = get(surveyIdAtom);
  if (!surveyId) return false;
  return get(isSurveyCompletedAtomFamily(surveyId));
});

/** Has this step been completed? */
export const isStepCompletedAtomFamily = atomFamily((stepId: string) =>
  atom((get) => get(completedStepIdsAtom).has(stepId))
);

/** Has this survey been started? */
export const isSurveyStartedAtomFamily = atomFamily((surveyId: string) =>
  atom((get) => {
    return get(startedSurveyIdsAtom).has(surveyId);
  })
);

/** Has this survey been completed? */
export const isSurveyCompletedAtomFamily = atomFamily((surveyId: string) =>
  atom((get) => get(completedSurveyIdsAtom).has(surveyId))
);

/** Is this material blocked by any prior required, uncompleted material? */
export const isBlockedByPriorRequiredAtomFamily = atomFamily((position: number) =>
  atom((get) => {
    const materials: Material[] = get(materialsAtom);
    const completedSurveyIds: Set<string> = get(completedSurveyIdsAtom);

    for (const m of materials) {
      if (m.position < position && m.isRequired && !completedSurveyIds.has(m.survey)) {
        return true;
      }
    }
    return false;
  })
);

/** A list of Progression objects indicating which surveys and steps have been started and completed. */
export const progressionsAtom = atomWithReset<Progression[]>([]);

/** Atom storing the last error encountered when attempting to save progressions. */
export const progressionErrorAtom = atomWithReset<Error | null>(null);

/** Atom containing the set of progression IDs currently being saved. */
export const savingProgressionIdsAtom = atomWithReset<Set<string>>(new Set<string>());

/**
 * Determines the start time of the current step.
 *
 * Behavior:
 * - If the timer is paused, return null
 * - If the step already has a "start" progression, return the earliest createdAt date for that progression.
 * - If the step has no start progression, return the current time.
 */
export const startTimeOfCurrentStepAtom = atom<Date | null>((get) => {
  const currentStepId: string | null = get(contextNavAtom)?.currentContext?.step ?? null;
  const isTimerPaused: boolean = get(isTimerPausedAtom);

  if (!currentStepId || isTimerPaused) return null;

  const earliestCreatedAt: Date | null = get(progressionsAtom)
    .filter((p: Progression) => p.step === currentStepId && p.type === 'S' && !p.deletedAt)
    .map(p => new Date(p.createdAt))
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

  return earliestCreatedAt ?? new Date();
});

/** Set of ids for started steps. */
export const startedStepIdsAtom = atom<Set<string>>((get) => {
  const progressions: Progression[] = get(progressionsAtom);
  const startedStepIds = new Set<string>();
  for (const p of progressions) {
    if (p.type === 'S' && !p.deletedAt && typeof p.step === 'string') startedStepIds.add(p.step);
  }
  return startedStepIds;
});

/** Set of ids for started surveys. */
export const startedSurveyIdsAtom = atom<Set<string>>((get) => {
  const progressions: Progression[] = get(progressionsAtom);
  const startedSurveyIds = new Set<string>();
  for (const p of progressions) {
    if (p.type === 'S' && !p.deletedAt && p.survey) startedSurveyIds.add(p.survey);
  }
  return startedSurveyIds;
});

/** Is there any completed material after the given one? */
export const percentCompleteAtomFamily = atomFamily((stepId: string) =>
  atom((get) => {
    const step: StepInfo | undefined = get(surveyViewAtom).data?.stepMap?.get(stepId);
    if (!step) return 0;

    const hasCompleted: boolean = get(completedStepIdsAtom).has(stepId);
    const answeredElementIds = get(answeredElementsAtom);
    const numberOfElementsAnswered: number = getNumberOfAnsweredElements(answeredElementIds, step.answerableElementIds);
    const numberOfAnswerableElements : number = step.answerableElementIds.size;

    if (hasCompleted || numberOfAnswerableElements === 0) return 100;

    return Math.min(100, (numberOfElementsAnswered / numberOfAnswerableElements) * 100);
  })
);

/** List of ids for the progressions that have not yet been saved to the API. */
export const unsavedProgressionIdsAtom = atomWithReset<Set<string>>(new Set<string>());